import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ResponsiveLoader from 'common/components/ResponsiveLoader/ResponsiveLoader';
import {
  selectDraftVersion,
  selectSharedVersion,
} from 'features/analyses/analysisSlice';
import { getLinksFromNarratives } from 'features/narratives/narrativesUtilities';
import { putAnalysis } from 'features/analyses/analysesService';
import { ConnectedContentContextProvider } from 'features/narratives/Context/ConnectedContentContext';
import {
  viewAnalysisPath,
  reportPreviewPath,
} from 'common/services/urlService';
import useVersionToggle from 'common/hooks/useVersionToggle';
import useIsMountedRef from 'common/hooks/useIsMountedRef';
import LoadingError from 'common/components/LoadingError';
import { SyncedInfoProvider } from 'common/components/Autosync/SyncedInfoProvider.tsx';
import { loadAnalysisVersions } from '../../analysisSliceThunk';
import {
  getFreeformAnalysis,
  getFreeformAnalysisSelector,
} from '../../editFreeformAnalysisSlice';
import FreeformAnalysisForm from '../FreeformAnalysisForm/FreeformAnalysisForm';
import { SectionsProvider } from '../FreeformAnalysisForm/FreeformAnalysisFormContext';
import './EditFreeformAnalysis.scss';
import { getFakeClientUser } from '../../../../common/redux/security/loggedInUser/loggedInUserSlice';

const EditFreeformAnalysis = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isMountedRef = useIsMountedRef();
  const draftVersion = useSelector(selectDraftVersion);
  const sharedVersion = useSelector(selectSharedVersion);
  const isFakeClientUser = useSelector(getFakeClientUser);

  const { commentCounts } = useVersionToggle(
    isMountedRef.current,
    draftVersion,
    sharedVersion
  );

  const { projectId, analysisId } = useParams();
  const [connectedContentIsChanged, setConnectedContentIsChanged] =
    useState(false);
  const [isAutoSaveCalled, setAutoSaveCalled] = useState(false);

  const { loading, error, analysis } = useSelector(getFreeformAnalysisSelector);
  const contextStatusInfoItem = useMemo(
    () => ({
      isViewMode: false,
      isEditMode: true,
      versionStatus: analysis?.versionStatus,
      userViewStatus: analysis?.userViewStatus,
      shareWithAll: analysis?.shareWithAll,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analysisId, sharedVersion]
  );
  useEffect(() => {
    dispatch(
      getFreeformAnalysis({
        projectId,
        analysisId,
        contextStatusInfoItem,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, analysisId, dispatch]);

  useEffect(() => {
    dispatch(loadAnalysisVersions(projectId, analysisId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId, dispatch, projectId, isAutoSaveCalled]);

  const setConnectedContentChanged = () => {
    setConnectedContentIsChanged(!connectedContentIsChanged);
  };

  const analysisContentId = useMemo(() => {
    if (analysis && analysis.contentId) {
      return analysis.contentId;
    }
    return null;
  }, [analysis]);

  const redirectToAnalysisDetailPage = useCallback(
    () => history.push(viewAnalysisPath(projectId, analysisId)),
    [history, analysisId, projectId]
  );

  const redirectToAnalysisPreview = (version) => {
    history.push(reportPreviewPath(projectId, analysisId, version));
  };

  const handleRedirectToView = (hasError) => {
    if (hasError) {
      history.replace(viewAnalysisPath(projectId, analysisId), {
        autoSaveError: hasError,
      });
    } else {
      redirectToAnalysisDetailPage();
    }
  };

  const handlePutAnalysis = async (
    narratives,
    analysisDto,
    lastSelectedKFA
  ) => {
    analysisDto.links = getLinksFromNarratives(
      narratives,
      analysis.contentId,
      lastSelectedKFA
    );
    await putAnalysis(projectId, analysisContentId, analysisDto);
  };

  const isOnlySharedAvailable = sharedVersion && draftVersion === undefined;

  const publishAction = async (
    analysisDto,
    narratives,
    lastSelectedKFA = null
  ) => {
    await handlePutAnalysis(narratives, analysisDto, lastSelectedKFA);
    if (isOnlySharedAvailable) {
      setAutoSaveCalled(true);
    }
  };

  const previewAction = async (
    analysisDto,
    narratives,
    lastSelectedKFA = null
  ) => {
    await handlePutAnalysis(narratives, analysisDto, lastSelectedKFA);
    redirectToAnalysisPreview('latest');
  };

  useEffect(() => {
    if (sharedVersion && isFakeClientUser) {
      redirectToAnalysisDetailPage();
    }
  }, [sharedVersion, isFakeClientUser, redirectToAnalysisDetailPage]);

  if (sharedVersion && isFakeClientUser) {
    return null;
  }

  if (loading)
    return (
      <div className="edit-freeform-loader-container">
        <ResponsiveLoader dataTestId="infinite-data-loader" />
      </div>
    );

  if (error) {
    return <LoadingError />;
  }

  const tags = analysis.tags ? analysis.tags.map((tag) => tag.text) : [];

  return (
    <>
      <SectionsProvider value={{ initialSections: analysis.sections }}>
        <SyncedInfoProvider value={contextStatusInfoItem}>
          <ConnectedContentContextProvider>
            <FreeformAnalysisForm
              projectId={projectId}
              analysisTitle={analysis.title}
              analysisInsight={analysis.insight}
              analysisBody={analysis.body}
              isAutoSaveCalled={isAutoSaveCalled}
              analysis={analysis}
              publishAction={publishAction}
              previewAction={previewAction}
              cancelAction={redirectToAnalysisDetailPage}
              formTitle="Edit Analysis"
              appliedTags={tags}
              onSaveText="Updating..."
              standbyText="Update"
              parentContentId={analysis.contentId}
              sections={analysis.sections}
              draftVersion={draftVersion}
              sharedVersion={sharedVersion}
              commentCounts={commentCounts}
              setConnectedContentChanged={setConnectedContentChanged}
              handleRedirectToView={handleRedirectToView}
            />
          </ConnectedContentContextProvider>
        </SyncedInfoProvider>
      </SectionsProvider>
    </>
  );
};

export default EditFreeformAnalysis;
